import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Snowflake, CheckCircle2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PROMO } from "@/lib/site";
import { Reveal } from "@/components/landing/Reveal";

const END = new Date(PROMO.endsISO);
const FALL_IMG_FALLBACK =
  "https://images.unsplash.com/photo-1601276174812-63280a55656e?crop=entropy&cs=srgb&fm=jpg&q=85";

const useCountdown = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, END.getTime() - now);
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff / 3600000) % 24),
    m: Math.floor((diff / 60000) % 60),
    s: Math.floor((diff / 1000) % 60),
  };
};

const TimeBox = ({ value, label }) => (
  <div className="flex flex-col items-center rounded-2xl border border-fall-border bg-white px-4 py-3 shadow-sm">
    <span className="font-display text-2xl font-extrabold tabular-nums text-brand-navy sm:text-3xl">
      {String(value).padStart(2, "0")}
    </span>
    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
  </div>
);

export const FallSpecial = () => {
  const { d, h, m, s } = useCountdown();
  const [form, setForm] = useState({ name: "", phone: "", comforters: 2 });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const winter = PROMO.key === "winter";
  const SeasonIcon = winter ? Snowflake : Leaf;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/fall-special/claims", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        comforters: Number(form.comforters),
      });
      setDone(true);
      toast.success(`${PROMO.name} locked in — we'll call to schedule your free pickup!`);
    } catch {
      toast.error("Something went wrong — please call 480-940-6023 instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="fall-special" data-testid="fall-special-section" className={`relative overflow-hidden py-24 lg:py-32 ${winter ? "bg-sky-50" : "bg-fall-bg"}`}>
      <SeasonIcon className="animate-float-soft absolute right-[6%] top-16 hidden h-12 w-12 text-fall-orange/20 lg:block" />
      <SeasonIcon className="animate-float-soft absolute bottom-20 left-[4%] hidden h-9 w-9 text-fall-amber/30 lg:block" style={{ animationDelay: "1.5s" }} />

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-fall-orange sm:text-sm">
            Limited time · ends {PROMO.endsLabel}
          </p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
            The {PROMO.name} is on: <span className="text-fall-orange">comforters &amp; blankets</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            {winter
              ? "Cold nights are here — get the whole house's bedding and winter coats professionally cleaned, with free pickup & delivery on every order."
              : "Before the cold nights hit, get the whole house's bedding professionally cleaned — with free pickup & delivery on every order."}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-8 flex justify-center gap-2 sm:gap-3">
          <div data-testid="fall-countdown" className="flex gap-2 sm:gap-3">
            <TimeBox value={d} label="Days" />
            <TimeBox value={h} label="Hours" />
            <TimeBox value={m} label="Min" />
            <TimeBox value={s} label="Sec" />
          </div>
        </Reveal>

        <div className="mt-14 grid items-stretch gap-6 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <div className="grid h-full gap-6 sm:grid-cols-2">
              <div
                data-testid="fall-price-bulk"
                className="relative flex flex-col justify-center rounded-3xl border-2 border-fall-amber bg-white p-8 shadow-[0_0_30px_-5px_rgba(245,158,11,0.35)]"
              >
                <span className="absolute -top-4 right-6 rotate-6 rounded-full bg-fall-orange px-4 py-1.5 font-display text-xs font-extrabold uppercase tracking-widest text-white shadow-lg">
                  Best Deal
                </span>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">2 or more comforters</p>
                <p className="mt-3 font-display text-5xl font-extrabold text-brand-navy sm:text-6xl">
                  $19.99
                  <span className="ml-2 align-middle text-base font-bold text-slate-500">each</span>
                </p>
                <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-emerald-600">
                  <Truck className="h-4 w-4" /> Free pickup &amp; delivery included
                </p>
              </div>

              <div
                data-testid="fall-price-single"
                className="flex flex-col justify-center rounded-3xl border border-fall-border bg-white/70 p-8"
              >
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Just 1 comforter</p>
                <p className="mt-3 font-display text-5xl font-extrabold text-brand-navy sm:text-6xl">
                  $25
                  <span className="ml-2 align-middle text-base font-bold text-slate-500">per piece</span>
                </p>
                <p className="mt-3 text-sm font-semibold text-slate-500">
                  Add one more and they&apos;re $19.99 each
                </p>
              </div>

              <div className="overflow-hidden rounded-3xl border border-fall-border shadow-sm sm:col-span-2">
                <img
                  data-testid="fall-special-image"
                  src={winter ? "/images/winter_special.webp" : "/images/fall_special.webp"}
                  onError={(e) => (e.currentTarget.src = winter ? "/images/winter_special.png" : "/images/fall_special.png")}
                  alt="Freshly folded seasonal comforters"
                  className="h-56 w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-64"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="flex h-full flex-col rounded-3xl bg-gradient-to-br from-brand-navy via-brand-navy-light to-brand-navy p-8 text-white shadow-xl">
              {done ? (
                <div data-testid="fall-claim-success" className="flex h-full flex-col items-center justify-center text-center">
                  <CheckCircle2 className="h-14 w-14 text-emerald-400" />
                  <h3 className="mt-4 font-display text-xl font-bold">You&apos;re on the list!</h3>
                  <p className="mt-2 text-sm text-slate-300">
                    We&apos;ll call {form.phone} shortly to schedule your free pickup at the {PROMO.name} price.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-xl font-bold">Lock in the {PROMO.name}</h3>
                  <p className="mt-2 text-sm text-slate-300">
                    Drop your details — we&apos;ll call to schedule your free pickup. Offer valid through {PROMO.endsLabel}.
                  </p>
                  <form onSubmit={submit} className="mt-6 flex flex-1 flex-col gap-3">
                    <input
                      data-testid="fall-claim-name-input"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold placeholder:text-slate-400 focus:border-fall-amber focus:outline-none"
                    />
                    <input
                      data-testid="fall-claim-phone-input"
                      required
                      minLength={7}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Phone number"
                      className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold placeholder:text-slate-400 focus:border-fall-amber focus:outline-none"
                    />
                    <input
                      data-testid="fall-claim-email-input"
                      type="email"
                      value={form.email || ""}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Email (optional — get a reminder before it ends)"
                      className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold placeholder:text-slate-400 focus:border-fall-amber focus:outline-none"
                    />
                    <select
                      data-testid="fall-claim-comforters-select"
                      value={form.comforters}
                      onChange={(e) => setForm({ ...form, comforters: e.target.value })}
                      className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold text-white focus:border-fall-amber focus:outline-none [&>option]:text-brand-navy"
                    >
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <option key={n} value={n}>
                          {n === 6 ? "6+ comforters / blankets" : `${n} comforter${n > 1 ? "s" : ""} / blanket${n > 1 ? "s" : ""}`}
                        </option>
                      ))}
                    </select>
                    <button
                      data-testid="fall-claim-submit-button"
                      type="submit"
                      disabled={sending}
                      className="mt-auto rounded-xl bg-fall-amber px-5 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-brand-navy transition-transform hover:scale-[1.02] disabled:opacity-60"
                    >
                      {sending ? "Locking it in…" : `Claim the ${PROMO.name}`}
                    </button>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
